import { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Slide,
  useTheme,
  alpha,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  Close as CloseIcon,
  GetApp as GetAppIcon,
  Speed as SpeedIcon,
  CloudOff as CloudOffIcon,
  PhoneAndroid as PhoneAndroidIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'
import { usePWA } from '@/hooks/usePWA'

export default function InstallPrompt() {
  const theme = useTheme()
  const { showPrompt, isStandalone, installApp, dismissPrompt } = usePWA()
  const [isInstalling, setIsInstalling] = useState(false)

  // Don't show if already installed or prompt is dismissed
  if (!showPrompt || isStandalone) {
    return null
  }

  const handleInstall = async () => {
    setIsInstalling(true)
    try {
      const success = await installApp()
      if (!success) {
        setIsInstalling(false)
      }
    } catch (error) {
      console.error('Install failed:', error)
      setIsInstalling(false)
    }
  }

  const handleDismiss = () => {
    dismissPrompt()
  }

  return (
    <Slide direction="up" in={showPrompt} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 80, sm: 24 },
          left: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          maxWidth: 480,
          mx: 'auto',
          zIndex: theme.zIndex.snackbar,
        }}
      >
        <Paper
          elevation={8}
          sx={{
            position: 'relative',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            borderRadius: 5,
            overflow: 'hidden',
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.4)}`,
          }}
        >
          {/* Close button */}
          <IconButton
            size="small"
            onClick={handleDismiss}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              bgcolor: alpha('#fff', 0.1),
              '&:hover': {
                bgcolor: alpha('#fff', 0.2),
              },
              zIndex: 1,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box sx={{ p: 3 }}>
            {/* App icon and title */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha('#000', 0.2)}`,
                }}
              >
                <GetAppIcon
                  sx={{
                    fontSize: 32,
                    color: theme.palette.primary.main,
                  }}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 0.5,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  Install Happy House
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.95,
                    fontSize: '0.875rem',
                  }}
                >
                  Add to your home screen for quick access
                </Typography>
              </Box>
            </Box>

            {/* Benefits list */}
            <List dense sx={{ mb: 2, mt: 2 }}>
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 2,
                      bgcolor: alpha('#fff', 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <SpeedIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary="Faster loading times"
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: { fontWeight: 500, opacity: 0.95 },
                  }}
                />
              </ListItem>

              <ListItem sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 2,
                      bgcolor: alpha('#fff', 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CloudOffIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary="Works offline"
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: { fontWeight: 500, opacity: 0.95 },
                  }}
                />
              </ListItem>

              <ListItem sx={{ px: 0, py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 2,
                      bgcolor: alpha('#fff', 0.2),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PhoneAndroidIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary="Easy access from home screen"
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: { fontWeight: 500, opacity: 0.95 },
                  }}
                />
              </ListItem>
            </List>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleInstall}
                disabled={isInstalling}
                startIcon={isInstalling ? null : <CheckCircleIcon />}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: `0 4px 12px ${alpha('#000', 0.2)}`,
                  '&:hover': {
                    bgcolor: alpha('#fff', 0.95),
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 16px ${alpha('#000', 0.25)}`,
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  '&.Mui-disabled': {
                    bgcolor: alpha('#fff', 0.7),
                    color: alpha(theme.palette.primary.main, 0.6),
                  },
                }}
              >
                {isInstalling ? 'Installing...' : 'Install App'}
              </Button>

              <Button
                variant="text"
                size="large"
                onClick={handleDismiss}
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  py: 1.5,
                  px: 3,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontSize: '1rem',
                  bgcolor: alpha('#fff', 0.1),
                  '&:hover': {
                    bgcolor: alpha('#fff', 0.2),
                  },
                }}
              >
                Not Now
              </Button>
            </Box>
          </Box>

          {/* Decorative gradient overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '100%',
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.secondary.main,
                0.1
              )} 0%, transparent 50%)`,
              pointerEvents: 'none',
            }}
          />
        </Paper>
      </Box>
    </Slide>
  )
}
